import { z } from 'zod';
/**
 * Formationen: Startaufstellungen fuer die Trainingsplanung.
 *
 * - Standardformationen kommen aus dem Code (fuer alle Vereine), eigene gehoeren dem Verein.
 * - Eine Formation ist eine Liste von Positionen auf einem GANZEN Feld: eigenes Tor unten
 *   (Torwart bei y = 92), Spielrichtung nach oben. Koordinaten wie in der Skizze in Prozent
 *   des Spielfelds (0 links/oben, 100 rechts/unten), aber immer im Bereich 3 bis 97.
 * - Die Spielform (11, 9, 7 oder 5) legt fest, wie viele Spieler hoechstens dazugehoeren.
 */
const requiredText = (max) => z.string().trim().min(1, 'Pflichtfeld').max(max);
/** Spielformen: Grossfeld, Neuner, Siebener und Fuenfer (Funino-nah). */
export const FORMATION_GAME_SIZE = [11, 9, 7, 5];
export const FORMATION_GAME_SIZE_LABELS = {
    11: '11er',
    9: '9er',
    7: '7er',
    5: '5er',
};
/** Aufgabe einer Position - hilft beim Sortieren und Anzeigen, die Kuerzel selbst sind frei. */
export const FORMATION_ROLE = ['GK', 'DEF', 'MID', 'ATT'];
export const FORMATION_ROLE_LABELS = {
    GK: 'Torwart',
    DEF: 'Abwehr',
    MID: 'Mittelfeld',
    ATT: 'Angriff',
};
export const FORMATION_LIMITS = {
    name: 80,
    description: 500,
    /** Laenge des Kuerzels einer Position - gleich lang wie die Beschriftung eines Spielers in der Skizze. */
    label: 4,
    /** Kleinster und groesster Wert einer Koordinate: Spieler stehen nie auf der Linie. */
    coordinateMin: 3,
    coordinateMax: 97,
    /** Kleinster Abstand zweier Spieler in Zeichnungseinheiten (Breite 100) auf dem ganzen Feld. */
    minDistance: 6,
};
const coordinate = z.number().min(FORMATION_LIMITS.coordinateMin).max(FORMATION_LIMITS.coordinateMax);
export const formationPositionSchema = z.object({
    x: coordinate,
    y: coordinate,
    /** Kurzes Kuerzel (TW, IV, ZM ...) oder eine Nummer. */
    label: requiredText(FORMATION_LIMITS.label),
    role: z.enum(FORMATION_ROLE).optional(),
});
const gameSize = z.union([z.literal(11), z.literal(9), z.literal(7), z.literal(5)]);
const formationFields = {
    name: requiredText(FORMATION_LIMITS.name),
    gameSize,
    description: z.string().trim().max(FORMATION_LIMITS.description).default(''),
    positions: z.array(formationPositionSchema).min(1, 'Mindestens ein Spieler'),
};
/** Mehr Spieler als die Spielform erlaubt gibt es nicht - weniger sind erlaubt (unvollstaendige Aufstellung). */
function checkPlayers(value, ctx) {
    if (value.gameSize !== undefined && value.positions && value.positions.length > value.gameSize) {
        ctx.addIssue({ code: 'custom', path: ['positions'], message: `Höchstens ${value.gameSize} Spieler in dieser Spielform` });
    }
}
/** Eigene Formation anlegen. */
export const formationInputSchema = z.object(formationFields).superRefine(checkPlayers);
/** Das Schema einer Formation (so, wie sie gespeichert und gesendet wird). */
export const formationSchema = formationInputSchema;
/** Aendern: nur die mitgeschickten Felder. Der Server prueft das Ergebnis erneut. */
export const formationUpdateSchema = z.object(formationFields).partial().superRefine(checkPlayers);
/** Kopie einer Formation: optional mit eigenem Namen. */
export const formationCopySchema = z.object({
    name: requiredText(FORMATION_LIMITS.name).optional(),
});
export const FORMATION_SOURCE = ['CATALOG', 'OWN'];
export const formationListQuerySchema = z.object({
    /** Nur diese Spielform; ohne Angabe alle. */
    gameSize: z.preprocess((value) => (value === '' ? undefined : value), z.coerce.number().pipe(gameSize).optional()),
});
/**
 * Eine Standardformation. Der Schluessel (`<spielform>er-<system>`, z. B. `11er-4-4-2`) ist stabil.
 * Hier muss die Zahl der Spieler genau der Spielform entsprechen.
 */
export const catalogFormationSchema = z
    .object({
    key: z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)+$/, 'Schlüssel: Kleinbuchstaben, Ziffern und Bindestriche'),
    name: requiredText(FORMATION_LIMITS.name),
    gameSize,
    description: z.string().trim().min(1).max(FORMATION_LIMITS.description),
    positions: z.array(formationPositionSchema),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.positions.length !== value.gameSize) {
        ctx.addIssue({ code: 'custom', path: ['positions'], message: `Erwartet ${value.gameSize} Spieler, gefunden ${value.positions.length}` });
    }
});
export const CATALOG_FORMATION_ID_PREFIX = 'catalog:';
//# sourceMappingURL=training-formation.js.map